import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { DateTime } from "luxon";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { Link, useNavigate } from "react-router-dom";

import { Shipment } from "pwapi";

import { printShipment } from "app/actions/shippingActions";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { ReactComponent as LogoSVG } from "assets/logo.svg";
import {
  useSelectedLab,
  useSelectedLabStation,
} from "hooks/useSelectedLabStation";

const Shipments = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const selectedLabStation = useSelectedLabStation();
  const lab = useSelectedLab();
  const shipments = useAppSelector((sys) => sys.shipments);

  // const shipmentsReady = useRequestAfterEverySiteLoad(listShipments, {
  //   parentLookupLabStationId: selectedLabStation.id,
  // });

  // const clinics = useAppSelector((sys) => sys.clinics);
  // const users = useAppSelector((sys) => sys.users);

  const columnHelper = createColumnHelper<Shipment>();

  const columns = [
    columnHelper.accessor("shipped_at", {
      header: "Shipped",
      cell: ({ row }) => {
        return (
          row.original.created_at &&
          DateTime.fromISO(row.original.created_at).toLocaleString()
        );
      },
    }),

    columnHelper.accessor((row) => row.shipment_items?.length, {
      id: "Items",
      cell: ({ getValue }) => `${getValue()} Blocks`,
    }),
  ];

  const [printed, setPrinted] = useState(false);
  const onPrint = useCallback(() => {
    dispatch(
      printShipment({ parentLookupLabStationId: selectedLabStation.id })
    );
    setPrinted(true);
  }, [selectedLabStation.id]);

  useEffect(() => {
    window.addEventListener("afterprint", onPrint);

    return () => {
      window.removeEventListener("afterprint", onPrint);
    };
  }, []);

  const data = useMemo(
    () => [...shipments].sort((a, b) => (b.id || 0) - (a.id || 0)),
    [shipments]
  );

  const table = useReactTable({
    data,
    columns,

    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="Cases Shipping">
      <Helmet>
        <title>Previous Shipments</title>
      </Helmet>
      <header className="Cases__header">
        <figure className="Logo">
          <LogoSVG />
        </figure>

        <div>
          <p>
            <strong>{lab?.name}</strong>
          </p>
          <p className="Screen">Shipping</p>
          <p className="Print">
            <strong>
              <code>Shipping Manifest</code>
            </strong>{" "}
            <span>{DateTime.now().toLocaleString()}</span>
          </p>
        </div>
      </header>
      <nav>
        <Link to="/cases">Cases</Link>
        <Link to="/users">Users</Link>
        <Link to="/shipping">
          <strong>Shipping</strong>
        </Link>
      </nav>

      {!!shipments?.length && !printed ? (
        <div className="ShippingManifest">
          <div className="Cases__list">
            <table>
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th key={header.id}>
                        {header.isPlaceholder ? null : (
                          <div
                            {...{
                              className: header.column.getCanSort()
                                ? "cursor-pointer select-none"
                                : "",
                              onClick: header.column.getToggleSortingHandler(),
                            }}
                          >
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                          </div>
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map((row) => (
                  <tr key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        className={`Cases__td Cases__td--${cell} Cases__td--shipment`}
                        onClick={() => {
                          navigate(
                            `/station/shipping/shipments/${row.original.id}`
                          );
                        }}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="Shipping__empty">
          <p>There Have Been No Previous Shipments</p>
        </div>
      )}
    </div>
  );
};

export default Shipments;
